import { useUser } from '@clerk/react';
import { UploadSimple } from '@phosphor-icons/react';
import { Avatar, Button, Inline, Stack, toast } from '@reelplicate/ui';
import { useRef, useState } from 'react';

import { imageProblem } from '@/lib/upload';

/** What Clerk takes for a profile image that our copy promises. */
const PHOTO_TYPES = ['image/jpeg', 'image/png'];

/**
 * The "Profile photo" row of Settings → Account & security. The photo lives
 * in Clerk, not our storage: the file goes straight to Clerk, which then
 * serves it as `user.imageUrl` everywhere Clerk's user shows up.
 */
export function ProfilePhoto({ name }: { name: string }) {
  const { user } = useUser();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<'upload' | 'remove' | null>(null);

  const upload = async (file: File) => {
    if (!user) return;
    if (!PHOTO_TYPES.includes(file.type)) {
      toast.error('Use a JPG or PNG image.');
      return;
    }
    const problem = await imageProblem(file, { what: 'photo', minPx: 200 });
    if (problem) {
      toast.error(problem);
      return;
    }
    setBusy('upload');
    try {
      await user.setProfileImage({ file });
      // Clerk doesn't refresh the user after an image change.
      await user.reload();
      toast.success('Photo updated');
    } catch {
      toast.error('The upload didn’t go through. Try again.');
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!user) return;
    setBusy('remove');
    try {
      await user.setProfileImage({ file: null });
      await user.reload();
      toast.success('Photo removed');
    } catch {
      toast.error('Couldn’t remove your photo. Try again.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Inline gap={4} wrap className="py-5">
      <Avatar
        size={56}
        name={name}
        src={user?.hasImage ? user.imageUrl : undefined}
        alt=""
      />
      <Stack gap={0.5} className="min-w-0 flex-1">
        <p className="text-body-md-strong text-text-primary">Profile photo</p>
        <p className="text-body-sm text-text-secondary">JPG or PNG, at least 200 px.</p>
      </Stack>
      {user?.hasImage && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={busy === 'upload'}
          loading={busy === 'remove'}
          onClick={() => void remove()}
        >
          Remove
        </Button>
      )}
      <input
        ref={input}
        type="file"
        accept={PHOTO_TYPES.join(',')}
        className="sr-only"
        tabIndex={-1}
        aria-label="Photo file"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) void upload(file);
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        leadingIcon={UploadSimple}
        disabled={!user || busy === 'remove'}
        loading={busy === 'upload'}
        onClick={() => input.current?.click()}
      >
        Upload photo
      </Button>
    </Inline>
  );
}
